import { useEffect, useState } from "react";
import api from "@/lib/api";
import EditorLayout from "@/components/editor/EditorLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, MailOpen, Trash2, AlertTriangle, Inbox, Users, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const [items, setItems] = useState(null);
  const [subs, setSubs] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  const [delAck, setDelAck] = useState(false);

  const load = () => api.get("/contact-submissions").then((r) => setItems(r.data)).catch(() => setItems([]));
  const loadSubs = () => api.get("/newsletter").then((r) => setSubs(r.data)).catch(() => setSubs([]));
  useEffect(() => { load(); loadSubs(); }, []);

  const copyAll = () => {
    navigator.clipboard.writeText(subs.map((s) => s.email).join(", "));
    toast.success("כל כתובות המייל הועתקו");
  };
  const removeSub = async (id) => {
    try { await api.delete(`/newsletter/${id}?confirm=true`); setSubs((p) => p.filter((s) => s.id !== id)); toast.success("המנוי הוסר"); }
    catch { toast.error("ההסרה נכשלה"); }
  };

  const markRead = async (m) => {
    if (m.read) return;
    try { await api.put(`/contact-submissions/${m.id}/read`); setItems((p) => p.map((x) => (x.id === m.id ? { ...x, read: true } : x))); } catch {}
  };

  const doDelete = async () => {
    try { await api.delete(`/contact-submissions/${delTarget.id}?confirm=true`); toast.success("הפנייה נמחקה"); setDelTarget(null); load(); }
    catch { toast.error("המחיקה נכשלה"); }
  };

  return (
    <EditorLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">פניות מהאתר</h1>
        <p className="text-[#5C635C] mt-1">הודעות שהתקבלו מטופס יצירת הקשר.</p>
      </div>

      {/* Newsletter subscribers */}
      <div className="bg-white border border-[#E5E2D9] rounded-2xl p-6 mb-8" data-testid="subscribers-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F2F5F1] flex items-center justify-center"><Users className="w-5 h-5 text-[#4A5D4E]" /></div>
            <div>
              <p className="font-bold">מנויי ניוזלטר</p>
              <p className="text-sm text-[#5C635C]">{subs.length} נרשמו לעדכונים על קולקציות חדשות</p>
            </div>
          </div>
          {subs.length > 0 && (
            <button onClick={copyAll} data-testid="copy-emails-btn"
                    className="inline-flex items-center gap-2 text-sm text-[#4A5D4E] border border-[#E5E2D9] rounded-full px-4 py-2 hover:bg-[#F7F5F0] transition-colors">
              <Copy className="w-4 h-4" /> העתקת כל המיילים
            </button>
          )}
        </div>
        {subs.length === 0 ? (
          <p className="text-sm text-[#8a8f89]">עדיין אין נרשמים. הטופס נמצא בתחתית דף הנחיתה.</p>
        ) : (
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {subs.map((s) => (
              <span key={s.id} data-testid={`sub-${s.id}`} dir="ltr"
                    className="group inline-flex items-center gap-2 bg-[#F7F5F0] border border-[#E5E2D9] rounded-full pl-3 pr-2 py-1.5 text-sm">
                {s.email}
                <button onClick={() => removeSub(s.id)} data-testid={`sub-remove-${s.id}`} className="text-[#c8c4b8] hover:text-[#8C3A3A]"><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <h2 className="text-lg font-bold mb-4">פניות מטופס הקשר</h2>
      {items === null ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E2D9]">
          <Inbox className="w-10 h-10 mx-auto text-[#c8c4b8] mb-3" />
          <p className="text-[#5C635C]">אין פניות עדיין.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`message-${m.id}`} onClick={() => markRead(m)}
                 className={`bg-white border rounded-xl p-5 cursor-pointer transition-colors ${m.read ? "border-[#E5E2D9]" : "border-[#4A5D4E] bg-[#FBFCFB]"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#F2F5F1] flex items-center justify-center shrink-0">
                    {m.read ? <MailOpen className="w-4 h-4 text-[#5C635C]" /> : <Mail className="w-4 h-4 text-[#4A5D4E]" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold">{m.name} {!m.read && <span className="text-xs text-[#4A5D4E]">• חדש</span>}</p>
                    <a href={`mailto:${m.email}`} onClick={(e) => e.stopPropagation()} className="text-sm text-[#4A5D4E] hover:underline" dir="ltr">{m.email}</a>
                    <p className="text-[#1A1D1A] mt-2 whitespace-pre-line">{m.message}</p>
                    <p className="text-xs text-[#8a8f89] mt-2">{new Date(m.created_at).toLocaleString("he-IL")}</p>
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setDelAck(false); setDelTarget(m); }} data-testid={`message-delete-${m.id}`}
                        className="p-2 rounded-lg hover:bg-red-50 text-[#8C3A3A] shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent dir="rtl" className="font-editor" data-testid="message-delete-dialog">
          <DialogHeader><DialogTitle className="text-right flex items-center gap-2 text-[#8C3A3A]"><AlertTriangle className="w-5 h-5" /> מחיקת פנייה</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-[#5C635C]">
            <p className="bg-red-50 border border-red-100 rounded-lg p-3 text-[#8C3A3A]">הפנייה תימחק לצמיתות ולא ניתן יהיה לשחזר אותה.</p>
            <label className="flex items-start gap-2">
              <input type="checkbox" data-testid="message-delete-ack" checked={delAck} onChange={(e) => setDelAck(e.target.checked)} className="accent-[#8C3A3A] w-4 h-4 mt-0.5" />
              אני מבינה שהפנייה תימחק.
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} className="rounded-full">ביטול</Button>
            <Button data-testid="message-delete-confirm" onClick={doDelete} disabled={!delAck} className="bg-[#8C3A3A] hover:bg-[#742f2f] rounded-full disabled:opacity-40">מחיקה</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EditorLayout>
  );
}
